import React, {useCallback, useEffect, useMemo, useState} from 'react'
import { BsFillTrashFill, BsPlusLg } from 'react-icons/bs'
import './ModalPodConfig.css'

const flattenConfigEntries = (value, parentKey = '') => {
    if (Array.isArray(value)) {
        return [{
            keyPath: parentKey,
            value,
            valueType: 'array',
        }];
    }

    if (value && typeof value === 'object') {
        return Object.entries(value)
            .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey, undefined, { sensitivity: 'base' }))
            .flatMap(([key, nestedValue]) => (
            flattenConfigEntries(nestedValue, parentKey ? `${parentKey}.${key}` : key)
        ));
    }

    return [{
        keyPath: parentKey,
        value,
        valueType: value === null ? 'null' : typeof value,
    }];
};

const getDisplayValue = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    if (Array.isArray(value)) {
        return JSON.stringify(value);
    }

    return String(value);
};

const getInputSize = (value) => {
    const length = getDisplayValue(value).length;
    return Math.max(1, Math.min(Math.max(length, 4), 80));
};

const getTextareaCols = (value) => {
    const serializedValue = getDisplayValue(value);
    const longestLine = serializedValue.split('\n').reduce((largest, line) => Math.max(largest, line.length), 0);
    return Math.max(12, Math.min(longestLine, 80));
};

const getDefaultValueForType = (valueType) => {
    switch (valueType) {
        case 'number':
            return 0;
        case 'boolean':
            return false;
        case 'string':
        default:
            return '';
    }
};

const normalizeKeyPath = (value) => String(value ?? '').trim();

const setNestedConfigValue = (configObject, keyPath, nextValue) => {
    const pathSegments = String(keyPath ?? '').split('.').filter(Boolean);
    if (pathSegments.length === 0) {
        return configObject;
    }

    const nextConfig = Array.isArray(configObject) ? [...configObject] : { ...(configObject ?? {}) };
    let cursor = nextConfig;

    pathSegments.forEach((segment, index) => {
        const isLeaf = index === pathSegments.length - 1;

        if (isLeaf) {
            cursor[segment] = nextValue;
            return;
        }

        const existingValue = cursor[segment];
        cursor[segment] = Array.isArray(existingValue) ? [...existingValue] : { ...(existingValue ?? {}) };
        cursor = cursor[segment];
    });

    return nextConfig;
};

const deleteNestedConfigValue = (configObject, keyPath) => {
    const pathSegments = String(keyPath ?? '').split('.').filter(Boolean);
    if (pathSegments.length === 0) {
        return configObject;
    }

    const nextConfig = Array.isArray(configObject) ? [...configObject] : { ...(configObject ?? {}) };
    let cursor = nextConfig;

    for (let index = 0; index < pathSegments.length - 1; index += 1) {
        const segment = pathSegments[index];
        const existingValue = cursor?.[segment];

        if (!existingValue || typeof existingValue !== 'object' || Array.isArray(existingValue)) {
            return nextConfig;
        }

        cursor[segment] = { ...existingValue };
        cursor = cursor[segment];
    }

    delete cursor[pathSegments[pathSegments.length - 1]];
    return nextConfig;
};

const renderConfigValueControl = (row, onValueChange, options = {}) => {
    const { className = '', sizeMultiplier = 1 } = options;

    if (row.valueType === 'boolean') {
        return (
            <input
                type='checkbox'
                checked={Boolean(row.value)}
                onChange={(event) => onValueChange(row.keyPath, event.target.checked)}
                aria-label={row.keyPath}
                className={className}
            />
        );
    }

    if (row.valueType === 'number') {
        return (
            <input
                type='number'
                value={getDisplayValue(row.value)}
                size={Math.max(1, Math.round(getInputSize(row.value) * sizeMultiplier))}
                onChange={(event) => onValueChange(row.keyPath, event.target.value === '' ? '' : Number(event.target.value))}
                className={className}
            />
        );
    }

    if (row.valueType === 'array') {
        return <textarea value={getDisplayValue(row.value)} cols={getTextareaCols(row.value)} rows={3} readOnly className={className} />;
    }

    return (
        <input
            type='text'
            value={getDisplayValue(row.value)}
            size={Math.max(1, Math.round(getInputSize(row.value) * sizeMultiplier))}
            onChange={(event) => onValueChange(row.keyPath, event.target.value)}
            className={className}
        />
    );
};

const ModalPodConfig = ({closeModal, machineId, podInfo}) => {
    const [encryptedConfig, setEncryptedConfig] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [newEntry, setNewEntry] = useState({
        keyPath: '',
        valueType: 'string',
        value: '',
    });

    const resetNewEntry = useCallback(() => {
        setNewEntry({
            keyPath: '',
            valueType: 'string',
            value: '',
        });
    }, []);

    const loadMachineConfig = useCallback(async () => {
        const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/machines/machine/' + machineId,
            {
                method: 'GET',
                headers: {
                    machine: '2',
                    'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            });

        if (!response.ok) {
            throw new Error('Unable to load pod config.');
        }

        const data = await response.json();
        const machineRecord = Array.isArray(data) ? data[0] : data;
        const nextEncryptedConfig = machineRecord?.encrypted;

        setEncryptedConfig(nextEncryptedConfig && typeof nextEncryptedConfig === 'object' ? nextEncryptedConfig : {});
    }, [machineId]);

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        setIsLoading(true);
        setErrorMessage('');
        setSaveMessage('');

        loadMachineConfig()
            .then(() => {
                if (!isMounted) {
                    return;
                }
            })
            .catch((error) => {
                console.error(error);
                if (isMounted) {
                    setErrorMessage('Unable to load pod config right now.');
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [loadMachineConfig]);

    const encryptedConfigRows = useMemo(() => {
        return flattenConfigEntries(encryptedConfig);
    }, [encryptedConfig]);

    const encryptedConfigKeySet = useMemo(() => {
        return new Set(encryptedConfigRows.map((row) => row.keyPath));
    }, [encryptedConfigRows]);

    const buildConfigWithPendingEntry = useCallback((baseConfig) => {
        const trimmedKeyPath = normalizeKeyPath(newEntry.keyPath);
        if (!trimmedKeyPath) {
            return baseConfig;
        }

        return setNestedConfigValue(baseConfig, trimmedKeyPath, newEntry.value);
    }, [newEntry.keyPath, newEntry.value]);

    const handleConfigValueChange = (keyPath, nextValue) => {
        setSaveMessage('');
        setErrorMessage('');
        setEncryptedConfig((previousConfig) => setNestedConfigValue(previousConfig, keyPath, nextValue));
    };

    const handleDeleteConfigValue = (keyPath) => {
        setSaveMessage('');
        setErrorMessage('');
        setEncryptedConfig((previousConfig) => deleteNestedConfigValue(previousConfig, keyPath));
    };

    const handleNewEntryTypeChange = (valueType) => {
        setSaveMessage('');
        setErrorMessage('');
        setNewEntry((previousEntry) => ({
            ...previousEntry,
            valueType,
            value: getDefaultValueForType(valueType),
        }));
    };

    const handleAddConfigEntry = () => {
        const trimmedKeyPath = normalizeKeyPath(newEntry.keyPath);
        if (!trimmedKeyPath) {
            setErrorMessage('Enter a key before adding a config value.');
            return;
        }

        if (encryptedConfigKeySet.has(trimmedKeyPath)) {
            setErrorMessage('That config key already exists.');
            return;
        }

        setSaveMessage('');
        setErrorMessage('');
        setEncryptedConfig((previousConfig) => setNestedConfigValue(previousConfig, trimmedKeyPath, newEntry.value));
        resetNewEntry();
    };

    const handleSave = async () => {
        setIsSaving(true);
        setErrorMessage('');
        setSaveMessage('');

        try {
            const trimmedKeyPath = normalizeKeyPath(newEntry.keyPath);
            if (trimmedKeyPath && encryptedConfigKeySet.has(trimmedKeyPath)) {
                throw new Error('That config key already exists.');
            }

            const encryptedConfigToSave = buildConfigWithPendingEntry(encryptedConfig);
            const patchPayload = { encrypted: encryptedConfigToSave };
            console.log('[pod-config] PATCH payload', patchPayload);

            const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/machines/machine/' + machineId,
                {
                    method: 'PATCH',
                    headers: {
                        machine: '2',
                        'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                        'content-type': 'application/json',
                    },
                    body: JSON.stringify(patchPayload),
                });

            if (!response.ok) {
                throw new Error('Unable to save pod config.');
            }

            await response.json();

            resetNewEntry();
            await loadMachineConfig();

            setSaveMessage('Config saved.');
        } catch (error) {
            console.error(error);
            setErrorMessage(error.message || 'Unable to save pod config right now.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className='pod-config-overlay' onClick={(event) => {
            if (event.target.className === 'pod-config-overlay') {
                closeModal();
            }
        }}>
            <div className='pod-config-modal'>
                <button className='pod-config-close-btn' onClick={closeModal} aria-label='Close pod config'>X</button>

                <div className='pod-config-header'>
                    <h3>Edit Config</h3>
                    <div className='pod-config-subtitle'>
                        Pod {podInfo?.id ?? machineId}{podInfo?.description ? ` | ${podInfo.description}` : ''}
                    </div>
                </div>

                {isLoading && <div className='pod-config-empty'>Loading config...</div>}
                {!isLoading && errorMessage && <div className='pod-config-error'>{errorMessage}</div>}
                {!isLoading && !errorMessage && saveMessage && <div className='pod-config-success'>{saveMessage}</div>}

                {!isLoading && !errorMessage && (
                    <section className='pod-config-card'>
                        <div className='pod-config-card-header'>
                            <h4>Encrypted Config</h4>
                        </div>

                        {encryptedConfigRows.length === 0 ? (
                            <div className='pod-config-empty'>No encrypted config found.</div>
                        ) : (
                            <div className='pod-config-table-wrap'>
                                <table className='pod-config-table'>
                                    <colgroup>
                                        <col className='pod-config-key-col' />
                                        <col className='pod-config-value-col' />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th className='pod-config-key-header'>Key</th>
                                            <th className='pod-config-value-header'>Value</th>
                                            <th className='pod-config-action-header'>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {encryptedConfigRows.map((row) => (
                                            <tr key={row.keyPath}>
                                                <td className='pod-config-key-cell'>{row.keyPath}</td>
                                                <td className='pod-config-value-cell'>{renderConfigValueControl(row, handleConfigValueChange)}</td>
                                                <td className='pod-config-action-cell'>
                                                    <button
                                                        type='button'
                                                        className='pod-config-icon-btn'
                                                        onClick={() => handleDeleteConfigValue(row.keyPath)}
                                                        title={`Delete ${row.keyPath}`}
                                                        aria-label={`Delete ${row.keyPath}`}
                                                    >
                                                        <BsFillTrashFill />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className='pod-config-key-cell'>
                                                <input
                                                    type='text'
                                                    value={newEntry.keyPath}
                                                    className='pod-config-key-input'
                                                    placeholder='new_key or nested.key'
                                                    onChange={(event) => {
                                                        setSaveMessage('');
                                                        setErrorMessage('');
                                                        setNewEntry((previousEntry) => ({
                                                            ...previousEntry,
                                                            keyPath: event.target.value,
                                                        }));
                                                    }}
                                                />
                                            </td>
                                            <td className='pod-config-value-cell'>
                                                <div className='pod-config-new-entry-controls'>
                                                    <select
                                                        value={newEntry.valueType}
                                                        className='pod-config-type-select'
                                                        onChange={(event) => handleNewEntryTypeChange(event.target.value)}
                                                    >
                                                        <option value='string'>String</option>
                                                        <option value='number'>Number</option>
                                                        <option value='boolean'>Boolean</option>
                                                    </select>

                                                    {renderConfigValueControl({
                                                        keyPath: '__new_entry__',
                                                        valueType: newEntry.valueType,
                                                        value: newEntry.value,
                                                    }, (_keyPath, nextValue) => {
                                                        setSaveMessage('');
                                                        setErrorMessage('');
                                                        setNewEntry((previousEntry) => ({
                                                            ...previousEntry,
                                                            value: nextValue,
                                                        }));
                                                    }, {
                                                        className: newEntry.valueType === 'string' ? 'pod-config-new-string-value-input' : '',
                                                        sizeMultiplier: newEntry.valueType === 'string' ? 3 : 1,
                                                    })}
                                                </div>
                                            </td>
                                            <td className='pod-config-action-cell'>
                                                <button
                                                    type='button'
                                                    className='pod-config-icon-btn pod-config-add-btn'
                                                    onClick={handleAddConfigEntry}
                                                    title='Add config entry'
                                                    aria-label='Add config entry'
                                                >
                                                    <BsPlusLg />
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                )}

                <div className='pod-config-actions'>
                    <button type='button' className='btn' onClick={handleSave} disabled={isLoading || isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
                    <button type='button' className='btn btn-cancel' onClick={closeModal}>Close</button>
                </div>
            </div>
        </div>
    )
}

export default ModalPodConfig